import { date } from 'configuration/dayjs'
import {
    ADVANCED_PRODUCT_TITLE,
    ADVANCED_TEXT,
    COUNTRIES,
    DATES,
    METRIC_FILTERS,
    PARENT_ASIN,
    SEGMENT_BRANDS,
    SEGMENTS,
    SELLER_NAME,
    SOV_ASINS,
    SOV_METADATA_BRANDS,
} from 'const/filters'
import { getSingleClauseFilterValue } from 'helpers/filter'
import { addToComplexTextFilter } from 'helpers/filters'
import { formatMetricFiltersForParams } from 'helpers/filters/metrics'
import { isNonEmptyArray } from 'helpers/typeGuard'
import { formatDateFromFilter } from 'helpers/utils'
import {
    FilterOption,
    FiltersState,
    MetricFilter,
    SingleClauseFilterValue,
} from 'types'

interface AsinSalesEstimateFactsParams {
    report_date_min?: string
    report_date_max?: string
    asin__in?: string
    parent_asin?: string | null
    segment_id__in?: string
    country__in?: string
    complex_text_filter?: string

    [key: `${'brand' | 'seller_name'}${SingleClauseFilterValue['operator']}`]:
        | string
        | string[]
        | number
        | undefined
}

const stringifyFilterOptions = (
    options: (FilterOption | MetricFilter | SingleClauseFilterValue)[]
): string => options.map((item) => item.value).join()

export const formatSegmentFactsApiFilterParams = (
    filters: FiltersState,
    _hasHourlyData = false,
    fiscalYearStartMonth?: number
): AsinSalesEstimateFactsParams => {
    let params: AsinSalesEstimateFactsParams = {}
    let complex_text_filter: any = null

    const dates = filters[DATES]
    const { start, end } = formatDateFromFilter(
        dates,
        date(),
        filters.rangeLag,
        fiscalYearStartMonth
    )
    params.report_date_min = start
    params.report_date_max = end

    const asins = filters[SOV_ASINS]
    if (isNonEmptyArray(asins)) {
        params.asin__in = stringifyFilterOptions(asins)
    }

    const parentAsin = filters[PARENT_ASIN]
    if (parentAsin) {
        params.parent_asin = parentAsin === 'none' ? null : parentAsin
    }

    const brands = filters[SOV_METADATA_BRANDS]
    if (isNonEmptyArray(brands)) {
        params.brand__in = stringifyFilterOptions(brands)
    }

    const segmentBrands = filters[SEGMENT_BRANDS]
    if (isNonEmptyArray(segmentBrands)) {
        const filterName = 'brand'
        const result = getSingleClauseFilterValue(segmentBrands)

        if (result && result.value.length > 0) {
            const { operator, value } = result
            params[`${filterName}${operator}`] = value
        }
    }

    const countries = filters[COUNTRIES]
    if (isNonEmptyArray(countries)) {
        params.country__in = stringifyFilterOptions(countries)
    }

    const metricFilters = filters[METRIC_FILTERS]
    if (isNonEmptyArray(metricFilters)) {
        params = { ...params, ...formatMetricFiltersForParams(metricFilters) }
    }

    const advancedText = filters[ADVANCED_TEXT]
    if (isNonEmptyArray(advancedText)) {
        complex_text_filter = addToComplexTextFilter(
            complex_text_filter,
            advancedText
        )
    }

    const segments = filters[SEGMENTS]
    if (isNonEmptyArray(segments)) {
        params.segment_id__in = stringifyFilterOptions(segments)
    }

    const advancedProductTitle = filters[ADVANCED_PRODUCT_TITLE]
    if (isNonEmptyArray(advancedProductTitle)) {
        complex_text_filter = addToComplexTextFilter(
            complex_text_filter,
            advancedProductTitle
        )
    }

    const sellerName = filters[SELLER_NAME]
    if (isNonEmptyArray(sellerName)) {
        const filterName = 'seller_name'
        const result = getSingleClauseFilterValue(sellerName, 'Unknown')

        if (result && result.value.length > 0) {
            const { operator, value } = result
            params[`${filterName}${operator}`] = value
        }
    }

    if (complex_text_filter !== null) {
        params.complex_text_filter = JSON.stringify(complex_text_filter)
    }

    return params
}
